import {
    IconDeviceDesktop,
    IconUser,
    IconBriefcase,
    IconHome,
    IconLock,
    IconChartBar,
    IconSettings,
    IconFileText,
    IconShield,
    IconDashboard,
    IconPaperclip,
    IconBug,
    IconCheck,
} from '@tabler/icons-react'
import { msg } from '@lingui/core/macro'
import type { MacroMessageDescriptor } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { useCurrentUser, useHasFeature, useHasSecurityGroup } from '@m3-tech/boss-core'
import {
    type SidebarNavItem,
    type SidebarNavLeafItem,
    type SidebarNavGroupItem,
    type SidebarNavParentItem,
} from '@m3-tech/boss-ui'
import { createTestIdScope } from '@m3-tech/boss-utils'
import { useEmployee } from '@/hooks/useEmployee'
import { useBcNeedsAttentionCounts } from '@/hooks/useBcNeedsAttentionCounts'
import { BC_ROUTE_LIST } from '@/constants/bcNeedsAttention'
import { bcErrorStatus } from '@/utils/bcNeedsAttention.utils'
import { canAccessBrokerPlatformPrivacyPolicy } from '@/pages/BrokerPlatformPrivacyPolicies/access'
import { canAccessLaDrcTemplates } from '@/utils/template.utils'
import { SIGNALR_USER_IDS } from '@/constants/signalrAccess'
import { ERROR_MANAGEMENT_USER_IDS } from '@/constants/errorManagementAccess'
import { DEV_SERVICE_STATUS_USER_IDS } from '@/constants/devServiceStatusAccess'
import { TEST_ID_BASE, TestIdPage } from '@/constants/testIds'
import {
    FEATURE_AUTO_COMMUNICATION,
    FEATURE_BROKER_RATES,
    FEATURE_COMMISSION_REPORTS_PASSWORD,
    FEATURE_CTP_PLATFORM_DEFAULTS,
    FEATURE_CTP_PLATFORM_DOCUMENTS,
    FEATURE_CTP_PLATFORM_EMAILS,
    FEATURE_CTP_PLATFORM_NOTIFICATIONS,
    FEATURE_CUSTOM_SIGNATURE,
    FEATURE_CUSTOMER_PORTAL_SETTINGS,
    FEATURE_CUSTOMER_PORTAL_SETTINGS_SUBITEM,
    FEATURE_GROSS_COMMISSION_VISIBILITY,
    FEATURE_INTEGRATION_SETTINGS,
    FEATURE_MAILCHIMP,
    FEATURE_MARKETING_CRM_SETTINGS,
    FEATURE_MARKETING_INFO,
    FEATURE_MOPOLO,
    FEATURE_NEW_PRESET_COMMISSION,
    FEATURE_PERSONAL_INFO,
    FEATURE_PERSONAL_SETTINGS,
    FEATURE_PROVINCIAL_TAX_RATE,
    FEATURE_REVENUE_SETTINGS,
    FEATURE_SECURITY_SETTINGS,
    FEATURE_SYSTEM_SETTINGS,
    FEATURE_USER_SETTINGS,
    FEATURE_WEBSITE,
} from '@/constants/featureIds'
import {
    SECURITY_GROUP_ADMIN,
    SECURITY_GROUP_QA_TESTING,
    SECURITY_GROUP_FEATURE_ADMIN,
    SECURITY_GROUP_ONESPAN_ESIGN_LAYOUTS,
    SECURITY_GROUP_PAYROLL_MANAGER,
    SECURITY_GROUP_TL_REVENUE_SETTING,
} from '@/constants/securityGroups'

type Gate = {
    featureId?: number
    featureIdAny?: number[]
    securityGroupId?: number
    securityGroupIdAny?: number[]
    hidden?: boolean
}

type GatedLeaf = SidebarNavLeafItem & Gate
type GatedGroup = Omit<SidebarNavGroupItem, 'children'> & Gate & { children: GatedLeaf[] }
type GatedParent = Omit<SidebarNavParentItem, 'children'> &
    Gate & { children?: Array<GatedGroup | GatedLeaf> }

function stripGate<T extends Gate>(node: T): Omit<T, keyof Gate> {
    const {
        featureId: _a,
        featureIdAny: _b,
        securityGroupId: _c,
        securityGroupIdAny: _d,
        hidden: _e,
        ...rest
    } = node
    void _a
    void _b
    void _c
    void _d
    void _e
    return rest
}

/**
 * Builds the settings sidebar navigation items, filtered by the current user's
 * features and security groups. Mirrors the legacy MortgageBOSS settings menu
 * (src/routes/main/settings/Settings.js) including Admin, Feature Management,
 * OneSpan E-Sign Layouts, and QA Testing sections.
 */
export function useSettingsNavItems(): SidebarNavItem[] {
    const { t } = useLingui()
    const tid = createTestIdScope(`${TEST_ID_BASE}-${TestIdPage.SETTINGS}`)

    const hasFeatureSystemSettings = useHasFeature(FEATURE_SYSTEM_SETTINGS)
    const hasFeaturePersonalSettings = useHasFeature(FEATURE_PERSONAL_SETTINGS)
    const hasFeaturePersonalInfo = useHasFeature(FEATURE_PERSONAL_INFO)
    const hasFeatureProvincialTaxRate = useHasFeature(FEATURE_PROVINCIAL_TAX_RATE)
    const hasFeatureMarketingCrm = useHasFeature(FEATURE_MARKETING_CRM_SETTINGS)
    const hasFeatureMarketingInfo = useHasFeature(FEATURE_MARKETING_INFO)
    const hasFeatureWebsite = useHasFeature(FEATURE_WEBSITE)
    const hasFeatureBrokerRates = useHasFeature(FEATURE_BROKER_RATES)
    const hasFeatureMopolo = useHasFeature(FEATURE_MOPOLO)
    const hasFeatureAutoCommunication = useHasFeature(FEATURE_AUTO_COMMUNICATION)
    const hasFeatureCustomSignature = useHasFeature(FEATURE_CUSTOM_SIGNATURE)
    const hasFeatureMailchimp = useHasFeature(FEATURE_MAILCHIMP)
    const hasFeatureUserSettings = useHasFeature(FEATURE_USER_SETTINGS)
    const hasFeatureSecuritySettings = useHasFeature(FEATURE_SECURITY_SETTINGS)
    const hasFeatureRevenueSettings = useHasFeature(FEATURE_REVENUE_SETTINGS)
    const hasFeatureCommissionReports = useHasFeature(FEATURE_COMMISSION_REPORTS_PASSWORD)
    const hasFeatureNewPresetCommission = useHasFeature(FEATURE_NEW_PRESET_COMMISSION)
    const hasFeatureGrossCommission = useHasFeature(FEATURE_GROSS_COMMISSION_VISIBILITY)
    const hasFeatureCustomerPortal = useHasFeature(FEATURE_CUSTOMER_PORTAL_SETTINGS)
    const hasFeatureCustomerPortalSubitem = useHasFeature(FEATURE_CUSTOMER_PORTAL_SETTINGS_SUBITEM)
    const hasFeatureCtpDefaults = useHasFeature(FEATURE_CTP_PLATFORM_DEFAULTS)
    const hasFeatureCtpDocuments = useHasFeature(FEATURE_CTP_PLATFORM_DOCUMENTS)
    const hasFeatureCtpEmails = useHasFeature(FEATURE_CTP_PLATFORM_EMAILS)
    const hasFeatureCtpNotifications = useHasFeature(FEATURE_CTP_PLATFORM_NOTIFICATIONS)
    const hasFeatureIntegrations = useHasFeature(FEATURE_INTEGRATION_SETTINGS)

    const { data: employeeData } = useEmployee()
    const canAccessPrivacyPolicy = canAccessBrokerPlatformPrivacyPolicy(employeeData)
    const canAccessTemplates = canAccessLaDrcTemplates(employeeData)

    const currentUser = useCurrentUser()
    const canAccessSignalR = SIGNALR_USER_IDS.has(currentUser?.id ?? -1)
    const canAccessErrorManagement = ERROR_MANAGEMENT_USER_IDS.has(currentUser?.id ?? -1)
    const canAccessDevServiceStatus = DEV_SERVICE_STATUS_USER_IDS.has(currentUser?.id ?? -1)

    const hasGroupAdmin = useHasSecurityGroup(SECURITY_GROUP_ADMIN)
    const hasGroupFeatureAdmin = useHasSecurityGroup(SECURITY_GROUP_FEATURE_ADMIN)
    const hasGroupOneSpan = useHasSecurityGroup(SECURITY_GROUP_ONESPAN_ESIGN_LAYOUTS)
    const hasGroupQaTesting = useHasSecurityGroup(SECURITY_GROUP_QA_TESTING)
    const hasGroupTlRevenue = useHasSecurityGroup(SECURITY_GROUP_TL_REVENUE_SETTING)
    const hasGroupPayrollManager = useHasSecurityGroup(SECURITY_GROUP_PAYROLL_MANAGER)

    // FF-2856: the menu badge. Only Payroll Managers can read it; a 403 hides the item.
    const { data: bcCounts, error: bcCountsError } = useBcNeedsAttentionCounts({
        enabled: hasGroupPayrollManager,
    })
    const bcCountsDenied = bcErrorStatus(bcCountsError) === 403
    const bcToReview = bcCounts?.toReview ?? 0
    const transactionsToReviewLabel =
        bcToReview > 0 ? t`Transactions to review (${bcToReview})` : t`Transactions to review`

    const featureLookup = new Map<number, boolean>([
        [FEATURE_SYSTEM_SETTINGS, hasFeatureSystemSettings],
        [FEATURE_PERSONAL_SETTINGS, hasFeaturePersonalSettings],
        [FEATURE_PERSONAL_INFO, hasFeaturePersonalInfo],
        [FEATURE_PROVINCIAL_TAX_RATE, hasFeatureProvincialTaxRate],
        [FEATURE_MARKETING_CRM_SETTINGS, hasFeatureMarketingCrm],
        [FEATURE_MARKETING_INFO, hasFeatureMarketingInfo],
        [FEATURE_WEBSITE, hasFeatureWebsite],
        [FEATURE_BROKER_RATES, hasFeatureBrokerRates],
        [FEATURE_MOPOLO, hasFeatureMopolo],
        [FEATURE_AUTO_COMMUNICATION, hasFeatureAutoCommunication],
        [FEATURE_CUSTOM_SIGNATURE, hasFeatureCustomSignature],
        [FEATURE_MAILCHIMP, hasFeatureMailchimp],
        [FEATURE_USER_SETTINGS, hasFeatureUserSettings],
        [FEATURE_SECURITY_SETTINGS, hasFeatureSecuritySettings],
        [FEATURE_REVENUE_SETTINGS, hasFeatureRevenueSettings],
        [FEATURE_COMMISSION_REPORTS_PASSWORD, hasFeatureCommissionReports],
        [FEATURE_NEW_PRESET_COMMISSION, hasFeatureNewPresetCommission],
        [FEATURE_GROSS_COMMISSION_VISIBILITY, hasFeatureGrossCommission],
        [FEATURE_CUSTOMER_PORTAL_SETTINGS, hasFeatureCustomerPortal],
        [FEATURE_CUSTOMER_PORTAL_SETTINGS_SUBITEM, hasFeatureCustomerPortalSubitem],
        [FEATURE_CTP_PLATFORM_DEFAULTS, hasFeatureCtpDefaults],
        [FEATURE_CTP_PLATFORM_DOCUMENTS, hasFeatureCtpDocuments],
        [FEATURE_CTP_PLATFORM_EMAILS, hasFeatureCtpEmails],
        [FEATURE_CTP_PLATFORM_NOTIFICATIONS, hasFeatureCtpNotifications],
        [FEATURE_INTEGRATION_SETTINGS, hasFeatureIntegrations],
    ])

    const groupLookup = new Map<number, boolean>([
        [SECURITY_GROUP_ADMIN, hasGroupAdmin],
        [SECURITY_GROUP_FEATURE_ADMIN, hasGroupFeatureAdmin],
        [SECURITY_GROUP_ONESPAN_ESIGN_LAYOUTS, hasGroupOneSpan],
        [SECURITY_GROUP_QA_TESTING, hasGroupQaTesting],
        [SECURITY_GROUP_TL_REVENUE_SETTING, hasGroupTlRevenue],
        [SECURITY_GROUP_PAYROLL_MANAGER, hasGroupPayrollManager],
    ])

    const visible = (gate: Gate): boolean => {
        if (gate.hidden === true) return false
        if (gate.featureId !== undefined && !featureLookup.get(gate.featureId)) return false
        if (gate.featureIdAny && !gate.featureIdAny.some((id) => featureLookup.get(id)))
            return false
        if (gate.securityGroupId !== undefined && !groupLookup.get(gate.securityGroupId))
            return false
        if (gate.securityGroupIdAny && !gate.securityGroupIdAny.some((id) => groupLookup.get(id)))
            return false
        return true
    }

    const tree: GatedParent[] = [
        // Legacy `Settings.js` uses `menu.unshift(...)` for this item, so it sits at
        // the very top of the sidebar — ABOVE Admin — and is a root-level entry, not
        // an Admin child. It is a childless parent carrying `path`, the same shape as
        // `system-settings` below. Gated on a single hardcoded employee id, matching
        // legacy exactly (no security group, no feature flag).
        {
            id: 'dev-service-status',
            label: t(msg`Dev Service Status`),
            icon: IconCheck,
            path: '/dev-service-status',
            testId: tid('dev-service-status'),
            hidden: !canAccessDevServiceStatus,
        },
        {
            id: 'qa-testing',
            label: t(msg`QA Service Run`),
            icon: IconBug,
            path: '/qa-testing',
            securityGroupId: SECURITY_GROUP_QA_TESTING,
            testId: tid('qa-service-run'),
        },
        {
            id: 'admin',
            label: t(msg`Admin`),
            icon: IconShield,
            // FF-2856 (D1): Admin opens for Admins and for Payroll Managers. A Payroll
            // Manager who is not an Admin sees only "Transactions to review", so every
            // other child carries the Admin gate itself.
            securityGroupIdAny: [SECURITY_GROUP_ADMIN, SECURITY_GROUP_PAYROLL_MANAGER],
            children: [
                {
                    id: 'website-settings',
                    label: t(msg`Website Settings`),
                    path: '/admin/website-settings',
                    securityGroupId: SECURITY_GROUP_ADMIN,
                    testId: tid('website-settings'),
                },
                {
                    id: 'admin-blogs',
                    label: t(msg`Blogs`),
                    path: '/admin/blogs',
                    securityGroupId: SECURITY_GROUP_ADMIN,
                    testId: tid('admin-blogs'),
                },
                {
                    id: 'admin-testimonials',
                    label: t(msg`Testimonials`),
                    path: '/admin/testimonials',
                    securityGroupId: SECURITY_GROUP_ADMIN,
                    testId: tid('admin-testimonials'),
                },
                {
                    id: 'admin-mortgage-services',
                    label: t(msg`Mortgage Services`),
                    path: '/admin/mortgage-services',
                    securityGroupId: SECURITY_GROUP_ADMIN,
                    testId: tid('admin-mortgage-services'),
                },
                {
                    id: 'admin-privacy-policy',
                    label: t(msg`Privacy Policy`),
                    path: '/admin/privacy-policy',
                    securityGroupId: SECURITY_GROUP_ADMIN,
                    testId: tid('admin-privacy-policy'),
                    hidden: !canAccessPrivacyPolicy,
                },
                {
                    id: 'brand-rates',
                    label: t(msg`Brand Rates`),
                    path: '/admin/brand-rates',
                    securityGroupId: SECURITY_GROUP_ADMIN,
                    testId: tid('brand-rates'),
                },
                {
                    id: 'land-transfer-tax',
                    label: t(msg`Land Transfer Tax`),
                    path: '/admin/land-transfer-tax',
                    securityGroupId: SECURITY_GROUP_ADMIN,
                    testId: tid('land-transfer-tax'),
                },
                {
                    id: 'corporate-content',
                    label: t(msg`Corporate Content`),
                    path: '/admin/corporate-content',
                    securityGroupId: SECURITY_GROUP_ADMIN,
                    testId: tid('corporate-content'),
                },
                {
                    id: 'announcements',
                    label: t(msg`Announcements`),
                    path: '/admin/announcements',
                    securityGroupId: SECURITY_GROUP_ADMIN,
                    testId: tid('announcements'),
                },
                {
                    id: 'admin-preset-commission',
                    label: t(msg`Admin Preset Commission`),
                    path: '/admin/preset-commission',
                    securityGroupId: SECURITY_GROUP_ADMIN,
                    testId: tid('admin-preset-commission'),
                },
                {
                    id: 'commission-visibility',
                    label: t(msg`Commission Visibility Definitions`),
                    path: '/admin/commission-visibility',
                    testId: tid('commission-visibility'),
                    securityGroupId: SECURITY_GROUP_FEATURE_ADMIN,
                    securityGroupIdAny: [SECURITY_GROUP_ADMIN],
                },
                {
                    id: 'programs',
                    label: t(msg`Programs`),
                    path: '/admin/programs',
                    securityGroupId: SECURITY_GROUP_ADMIN,
                    testId: tid('programs'),
                },
                {
                    id: 'data-transformation',
                    label: t(msg`Data Transformation`),
                    path: '/admin/data-transformation',
                    securityGroupId: SECURITY_GROUP_ADMIN,
                    testId: tid('data-transformation'),
                },
                {
                    id: 'data-import-summary',
                    label: t(msg`Data Import Summary`),
                    path: '/admin/data-import-summary',
                    securityGroupId: SECURITY_GROUP_ADMIN,
                    testId: tid('data-import-summary'),
                },
                {
                    id: 'admin-signalr',
                    label: t(msg`SignalR`),
                    path: '/admin/signalr',
                    securityGroupId: SECURITY_GROUP_ADMIN,
                    testId: tid('admin-signalr'),
                    hidden: !canAccessSignalR,
                },
                {
                    id: 'admin-error-management',
                    label: t(msg`Error Management`),
                    path: '/admin/error-management',
                    securityGroupId: SECURITY_GROUP_ADMIN,
                    testId: tid('admin-error-management'),
                    hidden: !canAccessErrorManagement,
                },
                {
                    id: 'transactions-to-review',
                    label: transactionsToReviewLabel,
                    path: BC_ROUTE_LIST,
                    testId: tid('transactions-to-review'),
                    securityGroupId: SECURITY_GROUP_PAYROLL_MANAGER,
                    hidden: bcCountsDenied,
                },
            ],
            testId: tid('admin'),
        },
        {
            id: 'system-settings',
            label: t(msg`System Settings`),
            icon: IconDeviceDesktop,
            path: '/system-settings',
            testId: tid('system-settings'),
            featureId: FEATURE_SYSTEM_SETTINGS,
        },
        {
            id: 'personal-settings',
            label: t(msg`Personal Settings`),
            icon: IconUser,
            featureId: FEATURE_PERSONAL_SETTINGS,
            children: [
                {
                    id: 'personal-information',
                    label: t(msg`Personal Information`),
                    path: '/personal-information',
                    testId: tid('personal-information'),
                    featureId: FEATURE_PERSONAL_INFO,
                },
                {
                    id: 'provincial-tax-rate',
                    label: t(msg`Provincial Tax Rate`),
                    path: '/provincial-tax-rate',
                    testId: tid('provincial-tax-rate'),
                    featureId: FEATURE_PROVINCIAL_TAX_RATE,
                },
            ],
            testId: tid('personal-settings'),
        },
        {
            id: 'marketing-crm-settings',
            label: t(msg`Marketing & CRM Settings`),
            icon: IconBriefcase,
            featureId: FEATURE_MARKETING_CRM_SETTINGS,
            children: [
                {
                    id: 'marketing-information',
                    label: t(msg`Marketing Information`),
                    path: '/marketing-information',
                    testId: tid('marketing-information'),
                    featureId: FEATURE_MARKETING_INFO,
                },
                {
                    id: 'website',
                    label: t(msg`Website`),
                    path: '/website',
                    testId: tid('website'),
                    featureId: FEATURE_WEBSITE,
                },
                {
                    id: 'broker-platform-blogs',
                    label: t(msg`Blogs`),
                    path: '/broker-platform-blogs',
                    testId: tid('broker-platform-blogs'),
                },
                {
                    id: 'broker-platform-testimonials',
                    label: t(msg`Testimonials`),
                    path: '/broker-platform-testimonials',
                    testId: tid('broker-platform-testimonials'),
                },
                {
                    id: 'broker-platform-mortgage-services',
                    label: t(msg`Mortgage Services`),
                    path: '/broker-platform-mortgage-services',
                    testId: tid('broker-platform-mortgage-services'),
                },
                {
                    id: 'broker-platform-privacy-policy',
                    label: t(msg`Privacy Policy`),
                    path: '/broker-platform-privacy-policy',
                    testId: tid('broker-platform-privacy-policy'),
                    hidden: !canAccessPrivacyPolicy,
                },
                {
                    id: 'broker-rates',
                    label: t(msg`Broker Rates`),
                    path: '/broker-rates',
                    testId: tid('broker-rates'),
                    featureId: FEATURE_BROKER_RATES,
                },
                {
                    id: 'mopolo',
                    label: t(msg`Mopolo`),
                    path: '/mopolo',
                    testId: tid('mopolo'),
                    featureId: FEATURE_MOPOLO,
                },
                {
                    id: 'auto-communication',
                    label: t(msg`Auto Communication`),
                    path: '/auto-communication',
                    testId: tid('auto-communication'),
                    featureId: FEATURE_AUTO_COMMUNICATION,
                },
                {
                    id: 'custom-signature',
                    label: t(msg`Custom Signature`),
                    path: '/custom-signature',
                    testId: tid('custom-signature'),
                    featureId: FEATURE_CUSTOM_SIGNATURE,
                },
                {
                    id: 'mailchimp',
                    label: t(msg`Mailchimp`),
                    path: '/mailchimp',
                    testId: tid('mailchimp'),
                    featureId: FEATURE_MAILCHIMP,
                },
            ],
            testId: tid('marketing-crm-settings'),
        },
        {
            id: 'user-settings',
            label: t(msg`User Settings`),
            icon: IconHome,
            path: '/user-settings',
            testId: tid('user-settings'),
            featureId: FEATURE_USER_SETTINGS,
        },
        {
            id: 'security-settings',
            label: t(msg`Security Settings`),
            icon: IconLock,
            featureId: FEATURE_SECURITY_SETTINGS,
            children: [
                {
                    id: 'pin-code',
                    label: t(msg`PIN Code`),
                    path: '/pin-code',
                    testId: tid('pin-code'),
                    featureId: FEATURE_SECURITY_SETTINGS,
                },
            ],
            testId: tid('security-settings'),
        },
        {
            id: 'revenue-settings',
            label: t(msg`Revenue Settings`),
            icon: IconChartBar,
            featureId: FEATURE_REVENUE_SETTINGS,
            children: [
                {
                    id: 'commission-reports-password',
                    label: t(msg`Commission Reports Password`),
                    path: '/commission-reports-password',
                    testId: tid('commission-reports-password'),
                    featureId: FEATURE_COMMISSION_REPORTS_PASSWORD,
                },
                {
                    id: 'new-preset-commission',
                    // Legacy `Settings.js` labels this entry "Preset Commission" (not "New
                    // Preset Commission") — the page's own heading already reads the same.
                    // The old Preset Commission screen was retired (C260315), so there is no
                    // second entry to disambiguate from. See AB#1682.
                    label: t(msg`Preset Commission`),
                    path: '/new-preset-commission',
                    testId: tid('new-preset-commission'),
                    featureId: FEATURE_NEW_PRESET_COMMISSION,
                },
                {
                    id: 'gross-commission',
                    label: t(msg`Gross Commission Visibility`),
                    path: '/gross-commission',
                    testId: tid('gross-commission'),
                    featureId: FEATURE_GROSS_COMMISSION_VISIBILITY,
                    securityGroupId: SECURITY_GROUP_TL_REVENUE_SETTING,
                },
            ],
            testId: tid('revenue-settings'),
        },
        {
            id: 'customer-portal',
            label: t(msg`Customer Portal Settings`),
            icon: IconSettings,
            // Ungated like legacy: the group shows when a child does. The nav
            // filter drops childless groups, so 120 on the header would only
            // hide children that 121-128 already gate.
            children: [
                {
                    id: 'customer-portal-overview',
                    label: t(msg`Overview`),
                    path: '/customer-portal',
                    testId: tid('customer-portal-overview'),
                    featureId: FEATURE_CUSTOMER_PORTAL_SETTINGS_SUBITEM,
                },
                {
                    id: 'ctp-platform-defaults',
                    label: t(msg`Defaults`),
                    path: '/ctp-platform-defaults',
                    testId: tid('ctp-platform-defaults'),
                    featureId: FEATURE_CTP_PLATFORM_DEFAULTS,
                },
                {
                    id: 'ctp-platform-documents',
                    label: t(msg`Documents`),
                    path: '/ctp-platform-documents',
                    testId: tid('ctp-platform-documents'),
                    featureId: FEATURE_CTP_PLATFORM_DOCUMENTS,
                },
                {
                    id: 'ctp-platform-emails',
                    label: t(msg`Emails`),
                    path: '/ctp-platform-emails',
                    testId: tid('ctp-platform-emails'),
                    featureId: FEATURE_CTP_PLATFORM_EMAILS,
                },
                {
                    id: 'ctp-platform-notifications',
                    label: t(msg`Notifications`),
                    path: '/ctp-platform-notifications',
                    testId: tid('ctp-platform-notifications'),
                    featureId: FEATURE_CTP_PLATFORM_NOTIFICATIONS,
                },
            ],
            testId: tid('customer-portal'),
        },
        {
            id: 'integrations',
            label: t(msg`Integration Settings`),
            icon: IconDeviceDesktop,
            path: '/integrations',
            testId: tid('integrations'),
            featureId: FEATURE_INTEGRATION_SETTINGS,
        },
        {
            id: 'template-management',
            label: t(msg`Template Management`),
            icon: IconFileText,
            children: [
                {
                    id: 'templates',
                    label: t(msg`LA / DRC`),
                    path: '/templates',
                    testId: tid('templates'),
                    // Legacy parity: LA/DRC shows only with the Hub or Deal-Review grant
                    // (C260725-C260727). Task Templates below stays always-visible.
                    hidden: !canAccessTemplates,
                },
                {
                    id: 'task-templates',
                    label: t(msg`Task Templates`),
                    path: '/task-templates',
                    testId: tid('task-templates'),
                },
            ],
            testId: tid('template-management'),
        },
        {
            id: 'feature-management',
            label: t(msg`Feature Management`),
            icon: IconDashboard,
            securityGroupId: SECURITY_GROUP_FEATURE_ADMIN,
            children: [
                {
                    id: 'feature-management-general',
                    label: t(msg`General`),
                    path: '/feature-management/general',
                    testId: tid('feature-management-general'),
                },
                {
                    id: 'feature-management-create-profile',
                    label: t(msg`Create User Profile`),
                    path: '/feature-management/create-profile',
                    testId: tid('feature-management-create-profile'),
                },
                {
                    id: 'feature-management-update-profile',
                    label: t(msg`Update User Profile`),
                    path: '/feature-management/update-profile',
                    testId: tid('feature-management-update-profile'),
                },
                {
                    id: 'feature-management-configured-profiles',
                    label: t(msg`Configured Profiles`),
                    path: '/feature-management/configured-profiles',
                    testId: tid('feature-management-configured-profiles'),
                },
            ],
            testId: tid('feature-management'),
        },
        {
            id: 'onespan-esign-layouts',
            label: t(msg`OneSpan E-Sign Layouts`),
            icon: IconPaperclip,
            securityGroupId: SECURITY_GROUP_ONESPAN_ESIGN_LAYOUTS,
            children: [
                {
                    id: 'onespan-layouts-list',
                    label: t(msg`Existing Layouts`),
                    path: '/onespan/layouts',
                    testId: tid('onespan-layouts-list'),
                },
                {
                    id: 'onespan-layouts-new',
                    label: t(msg`Add a Layout`),
                    path: '/onespan/layouts/new',
                    testId: tid('onespan-layouts-new'),
                },
            ],
            testId: tid('onespan-esign-layouts'),
        },
    ]

    const filterLeaves = (leaves: GatedLeaf[]): SidebarNavLeafItem[] =>
        leaves.filter(visible).map(stripGate)

    return tree
        .filter(visible)
        .map((parent): SidebarNavItem | null => {
            if (!parent.children) return stripGate(parent) as SidebarNavItem
            const filteredChildren = parent.children
                .map((child): SidebarNavGroupItem | SidebarNavLeafItem | null => {
                    if ('children' in child && child.children) {
                        if (!visible(child)) return null
                        const grand = filterLeaves(child.children)
                        if (grand.length === 0) return null
                        return { ...stripGate(child), children: grand } as SidebarNavGroupItem
                    }
                    return visible(child) ? (stripGate(child) as SidebarNavLeafItem) : null
                })
                .filter((x): x is SidebarNavGroupItem | SidebarNavLeafItem => x !== null)

            if (filteredChildren.length === 0) return null
            return { ...stripGate(parent), children: filteredChildren } as SidebarNavItem
        })
        .filter((x): x is SidebarNavItem => x !== null)
}

/**
 * Maps route path segments to translated page titles.
 * Used by PlaceholderPage to derive its heading from the current URL.
 */
export const ROUTE_TITLES: Record<string, MacroMessageDescriptor> = {
    'system-settings': msg`System Settings`,
    'personal-information': msg`Personal Information`,
    'provincial-tax-rate': msg`Provincial Tax Rate`,
    'marketing-information': msg`Marketing Information`,
    website: msg`Website`,
    'broker-platform-blogs': msg`Blogs`,
    'broker-platform-testimonials': msg`Testimonials`,
    'broker-platform-mortgage-services': msg`Mortgage Services`,
    'broker-platform-privacy-policy': msg`Privacy Policy`,
    'broker-rates': msg`Broker Rates`,
    mopolo: msg`Mopolo`,
    'auto-communication': msg`Auto Communication`,
    'custom-signature': msg`Custom Signature`,
    mailchimp: msg`Mailchimp`,
    'user-settings': msg`User Settings`,
    'pin-code': msg`PIN Code`,
    'commission-reports-password': msg`Commission Reports Password`,
    'gross-commission': msg`Gross Commission Visibility`,
    'new-preset-commission': msg`Preset Commission`,
    'customer-portal': msg`Customer Portal Settings`,
    'ctp-platform-defaults': msg`Defaults`,
    'ctp-platform-documents': msg`Documents`,
    'ctp-platform-emails': msg`Emails`,
    'ctp-platform-notifications': msg`Notifications`,
    integrations: msg`Integration Settings`,
    templates: msg`LA / DRC`,
    'task-templates': msg`Task Templates`,
    'website-settings': msg`Website Settings`,
    'brand-rates': msg`Brand Rates`,
    blogs: msg`Blogs`,
    testimonials: msg`Testimonials`,
    'mortgage-services': msg`Mortgage Services`,
    'privacy-policy': msg`Privacy Policy`,
    'land-transfer-tax': msg`Land Transfer Tax`,
    'corporate-content': msg`Corporate Content`,
    announcements: msg`Announcements`,
    'commission-visibility': msg`Commission Visibility Definitions`,
    programs: msg`Programs`,
    'data-transformation': msg`Data Transformation`,
    'data-import-summary': msg`Data Import Summary`,
    signalr: msg`SignalR`,
    'error-management': msg`Error Management`,
    'transactions-to-review': msg`Transactions to review`,
    'feature-management': msg`Feature Management`,
    'create-profile': msg`Create User Profile`,
    'update-profile': msg`Update User Profile`,
    'configured-profiles': msg`Configured Profiles`,
    layouts: msg`Existing Layouts`,
    new: msg`Add a Layout`,
    'qa-testing': msg`QA Service Run`,
    'dev-service-status': msg`Dev Service Status`,
}
